import { useCallback } from 'react';

import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import { useUpdateNotes } from '@/react-query/videa-notes/useUpdateNotes';
import type { VideaNoteSectionFieldsSnapshot } from '@/react-query/videa-notes/types';

import {
  BLURB_DELIMITER,
  createClearFieldsPayload,
  createNoteUpdatePayload,
  detectNoteChanges,
  formatConsentBlurb,
  getCurrentFields,
  getCurrentTemplate,
  getOriginalContent,
} from '../helpers';
import type { EditedFields } from '../types';

interface UseNoteHandlersProps {
  selectedNote: ClinicalNote | undefined;
  noteContent: string;
  noteWithTemplateData: ClinicalNote | undefined;
  hasWritebackEnabled: boolean;
  enableVoiceNotesConsent: boolean;
  editedFields: EditedFields;
  isResettingRef: React.MutableRefObject<boolean>;
  prevContentRef: React.MutableRefObject<string>;
  prevTemplateRef: React.MutableRefObject<string | null>;
  setNoteContent: (content: string) => void;
  setEditedFields: (
    fields: EditedFields | ((prev: EditedFields) => EditedFields),
  ) => void;
  refetchFullNote: () => Promise<unknown>;
}

export const useNoteHandlers = ({
  selectedNote,
  noteContent,
  noteWithTemplateData,
  hasWritebackEnabled,
  enableVoiceNotesConsent,
  editedFields,
  isResettingRef,
  prevContentRef,
  prevTemplateRef,
  setNoteContent,
  setEditedFields,
  refetchFullNote,
}: UseNoteHandlersProps) => {
  // Variables
  const { mutate: updateNote } = useUpdateNotes();

  // Functions
  const saveNoteBeforeClose = useCallback(() => {
    if (!selectedNote?.id) return;

    const changeDetection = detectNoteChanges(
      noteContent,
      noteWithTemplateData,
      editedFields,
      hasWritebackEnabled,
      prevContentRef,
      prevTemplateRef,
    );

    if (!changeDetection || !changeDetection.hasChanges) return;

    const { noteWithoutBlurb, isTemplateNote, fieldsChanged } = changeDetection;

    const updatePayload = createNoteUpdatePayload(
      noteWithoutBlurb,
      isTemplateNote,
      fieldsChanged,
      noteWithTemplateData,
      editedFields,
    );

    updateNote({
      clinicalNoteId: selectedNote.id,
      updateNotesBody: updatePayload,
    });
  }, [
    selectedNote?.id,
    noteContent,
    noteWithTemplateData,
    hasWritebackEnabled,
    editedFields,
    prevContentRef,
    prevTemplateRef,
    updateNote,
  ]);

  const resetNoteToOriginal = useCallback(() => {
    if (!selectedNote?.id) return;

    isResettingRef.current = true;

    // Capture current state before optimistic updates for rollback on error
    const currentContent = noteContent;
    const currentEditedFields = editedFields;
    const currentPrevContent = prevContentRef.current;
    const currentPrevTemplate = prevTemplateRef.current;

    const currentTemplate = getCurrentTemplate(noteWithTemplateData);
    const isTemplateNote = Boolean(
      currentTemplate && currentTemplate.length > 0,
    );
    const originalContentWithoutBlurb = getOriginalContent(
      noteWithTemplateData,
      selectedNote,
    );

    let contentToSet = originalContentWithoutBlurb;
    if (
      !hasWritebackEnabled &&
      selectedNote.patientConsent &&
      enableVoiceNotesConsent
    ) {
      const blurb = formatConsentBlurb(selectedNote.patientConsent);
      contentToSet = `${originalContentWithoutBlurb}${BLURB_DELIMITER}${blurb}`;
    }

    // Optimistic updates
    setNoteContent(contentToSet);
    setEditedFields(null);

    if (isTemplateNote) {
      prevTemplateRef.current = originalContentWithoutBlurb;
    } else {
      prevContentRef.current = originalContentWithoutBlurb;
    }

    // Use createNoteUpdatePayload to ensure consistency with other save paths
    // This includes editedSummary for template notes (rendered with default field values)
    const clearedFields: EditedFields = isTemplateNote
      ? createClearFieldsPayload(noteWithTemplateData) ?? null
      : null;
    const hasFieldsToClear = clearedFields !== null && clearedFields.length > 0;
    const updatePayload = createNoteUpdatePayload(
      originalContentWithoutBlurb,
      isTemplateNote,
      hasFieldsToClear,
      noteWithTemplateData,
      clearedFields,
    );

    updateNote(
      {
        clinicalNoteId: selectedNote.id,
        updateNotesBody: updatePayload,
      },
      {
        onSuccess: async () => {
          try {
            await refetchFullNote();
          } catch (error) {
            console.error('Error during reset refetch:', error);
          } finally {
            isResettingRef.current = false;
          }
        },
        onError: () => {
          // Rollback optimistic updates on error
          setNoteContent(currentContent);
          setEditedFields(currentEditedFields);
          prevContentRef.current = currentPrevContent;
          prevTemplateRef.current = currentPrevTemplate;
          isResettingRef.current = false;
        },
      },
    );
  }, [
    selectedNote,
    noteWithTemplateData,
    noteContent,
    editedFields,
    hasWritebackEnabled,
    enableVoiceNotesConsent,
    isResettingRef,
    prevContentRef,
    prevTemplateRef,
    setNoteContent,
    setEditedFields,
    refetchFullNote,
    updateNote,
  ]);

  const handleVariableValueChange = useCallback(
    (varName: string, newValue: string) => {
      if (!noteWithTemplateData?.id) return;

      // Variables
      const currentFields = getCurrentFields(noteWithTemplateData);
      if (!currentFields) return;

      let foundSection: VideaNoteSectionFieldsSnapshot | undefined;

      for (const section of currentFields) {
        const field = section.fields.find((f) => f.key === varName);
        if (field) {
          foundSection = section;
          break;
        }
      }

      if (!foundSection) return;

      setEditedFields((prev: EditedFields) => {
        const existing = prev ?? [];
        const sectionIndex = existing.findIndex(
          (s: { sectionId: string }) => s.sectionId === foundSection!.sectionId,
        );

        if (sectionIndex >= 0) {
          // Create immutable copy of the array
          const updated = [...existing];
          // Create immutable copy of the section
          const updatedSection = { ...updated[sectionIndex] };
          // Create immutable copy of the fields array
          const updatedFields = [...updatedSection.fields];

          const fieldIndex = updatedFields.findIndex(
            (f: { key: string }) => f.key === varName,
          );
          if (fieldIndex >= 0) {
            // Create immutable copy of the field and update it
            updatedFields[fieldIndex] = {
              ...updatedFields[fieldIndex],
              editedValue: newValue,
            };
          } else {
            // Add new field to the immutable copy
            updatedFields.push({
              key: varName,
              editedValue: newValue,
            });
          }

          // Update the section with the new fields array
          updatedSection.fields = updatedFields;
          // Update the array with the new section
          updated[sectionIndex] = updatedSection;
          return updated;
        }

        return [
          ...existing,
          {
            sectionId: foundSection!.sectionId,
            fields: [{ key: varName, editedValue: newValue }],
          },
        ];
      });
    },
    [noteWithTemplateData, setEditedFields],
  );

  return {
    saveNoteBeforeClose,
    resetNoteToOriginal,
    handleVariableValueChange,
  };
};
